import {
    AppId,
    CornerLabel,
    FCX,
    ID,
    ProductLogo,
    Tooltip,
    usePlatformFeatureFlag,
    useUuid
} from "@datassential/platform-ui-lib";
import { FoodSectionEnum } from "@pages/Food/Food";
import FoodSection from "@pages/Food/components/Section/Section";
import { AiOutlineInfoCircle } from "react-icons/ai";
import cn from "classnames";
import FoodWidget from "@pages/Food/components/Widget/Widget";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import './SectionSocial.scss';
import FoodSocialMentions from "@pages/Food/components/SectionSocial/components/SocialMentions/SocialMentions";
import FoodSocialAssociations
    from "@pages/Food/components/SectionSocial/components/SocialAssociations/SocialAssociations";
import { useFoodSocial } from "@apiHooks/useFoodSocial";
import { PermissionLevelEnum } from "@models/PermissionSection";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import FoodSocialPosts from "@pages/Food/components/SectionSocial/components/Posts/Posts";
import { useFoodSocialPosts } from "@apiHooks/useFoodSocialAssociationPosts";
import useNotification from "@hooks/useNotification";
import CopyButton from "@components/new/Table/CopyButton";
import CopyNotification from "@components/new/Table/CopyNotification";
import { trimInnerHtml, trimLineBreaks } from "@helpers";
import { SocialPosts, ISocialPost } from "@models/SocialPosts";
import dayjs from "dayjs";

export enum FoodSocialMetricEnum {
    Mentions = 'Mentions',
    Engagement = 'Engagement',
}

export enum FoodSocialSortFilterEnum {
    Engagement = 0,
    Newest = 1,
    Positive = 2,
    Negative = 3,
}

const COPY_DATA_CONFIG: Array<{
    key: keyof ISocialPost;
    title: string;
    valueFormatter?: (value: string) => string;
}> = [
    { key: 'source', title: 'Source' },
    { key: 'sentiment', title: 'Sentiment' },
    { key: 'engagement', title: 'Engagement Count' },
    { key: 'date', title: 'Date', valueFormatter: SocialPosts.formatDate },
    { key: 'text', title: 'Description', valueFormatter: (value) => trimLineBreaks(trimInnerHtml(value)) },
    { key: 'url', title: 'Post URL' },
]

const FoodSectionSocial: FCX<{
    word: string;
    wordId: ID;
    setHasData: (hasData: boolean) => void;
}> = ({
    word,
    wordId,
    setHasData,
}) => {
    const notification = useNotification();
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const isMinimalFiltersFeatureEnabled = usePlatformFeatureFlag('Snap Social Listening Minimal Filters');
    const [selectedMetric, setSelectedMetric] = useState<FoodSocialMetricEnum>(FoodSocialMetricEnum.Mentions);
    const [
        selectedSortFilterMetric,
        setSelectedSortFilterMetric,
    ] = useState<FoodSocialSortFilterEnum>(FoodSocialSortFilterEnum.Engagement);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const [selectedAssociation, setSelectedAssociation] = useState<string>('');
    const tooltipId = useUuid();
    const tabsTooltipId = useUuid();

    const onMentionBarClick = useCallback(
        (month: number | null, year: string | null) => {
            setSelectedMonth(month);
            setSelectedYear(year);
            setSelectedAssociation('');
        },
        [setSelectedMonth, setSelectedYear, setSelectedAssociation]
    );

    const onAssociationBarClick = useCallback(
        (association: string) => {
            setSelectedAssociation(association);
            setSelectedMonth(null);
            setSelectedYear(null);
        },
        [setSelectedAssociation, setSelectedMonth, setSelectedYear]
    );

    const isCustomPostsApplied = useMemo(
        () => selectedAssociation !== '' || selectedMonth !== null || selectedYear !== null,
        [selectedAssociation, selectedMonth, selectedYear]
    );

    const {
        isLoading,
        data: {
            hasData,
            permissionLevel,
            data,
        },
    } = useFoodSocial(wordId);

    const {
        isLoading: isAssociationPostsLoading,
        data: associationPostsData,
    } = useFoodSocialPosts(
        wordId,
        selectedAssociation,
        selectedYear,
        selectedMonth,
    );

    useEffect(() => {
        setHasData(!isLoading && hasData);
    }, [setHasData, isLoading, hasData]);

    const postsSource = useMemo(
        () => {
            return isCustomPostsApplied
                ? associationPostsData
                : data.posts;
        },
        [data, associationPostsData, isCustomPostsApplied]
    );

    const postsData = useMemo(
        () => {
            if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Negative) {
                return postsSource.negative;
            } else if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Positive) {
                return postsSource.positive;
            } else if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Newest) {
                return postsSource.newest;
            }

            return postsSource.regular;
        },
        [postsSource, selectedSortFilterMetric]
    );

    const postsDateRangeString = useMemo(
        (): ReactNode => {
            if (selectedMonth !== null && selectedYear !== null) {
                return <b>
                    {SocialPosts.getDateRange(
                        dayjs(`${selectedYear}-${selectedMonth}-02`),
                        dayjs(`${selectedYear}-${selectedMonth}-02`)
                    )}
                </b>;
            }

            return data.posts.dateRangeStr;
        },
        [data.posts.dateRangeStr, selectedMonth, selectedYear]
    )

    const copyPostsData = useMemo(
        (): ComponentProps<typeof CopyButton>['data'] => {
            return [
                COPY_DATA_CONFIG.map(({ title }) => title),
                ...postsData.map(
                    (post) => {
                        return COPY_DATA_CONFIG.map(
                            ({ key, valueFormatter }) => {
                                const value = post[key];
                                return valueFormatter ? valueFormatter(`${value}`) : `${value}`;
                            }
                        );
                    }
                )
            ]
        },
        [postsData]
    );

    const sortedByString = useMemo(
        () => {
            if (selectedSortFilterMetric === FoodSocialSortFilterEnum.Newest) {
                return 'by date';
            }

            return 'by engagement';
        },
        [selectedSortFilterMetric]
    );

    const hasAccess = permissionLevel === PermissionLevelEnum.Full;

    const postsTabs = useMemo(
        () => {
            if (isMinimalFiltersFeatureEnabled) {
                return [
                    { id: FoodSocialSortFilterEnum.Engagement, title: 'Engagement' },
                    { id: FoodSocialSortFilterEnum.Newest, title: 'Newest' },
                ];
            }
            return [
                { id: FoodSocialSortFilterEnum.Newest, title: 'Newest' },
                { id: FoodSocialSortFilterEnum.Positive, title: 'Positive' },
                { id: FoodSocialSortFilterEnum.Negative, title: 'Negative' },
            ];
        },
        [isMinimalFiltersFeatureEnabled],
    );

    const postsSectionKey = useMemo(
        () => `${selectedSortFilterMetric}-${selectedAssociation}-${selectedMonth}-${selectedYear}`,
        [selectedSortFilterMetric, selectedAssociation, selectedMonth, selectedYear]
    );

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (!hasData) return null;

    return (
        <div
            className="Food__section appears-top del-1"
            id={FoodSectionEnum.Social}
            data-testid="blockFoodSectionSocial"
        >
            <FoodSection
                data-testid="blockFoodSocial"
                className={cn(
                    "FoodSectionSocial",
                    permissionLevel !== PermissionLevelEnum.Full && 'is-blocked'
                )}
                headerContent={(
                    <div data-testid="textFoodSocialTitle" className="FoodSectionSocial__section-title">
                        SOCIAL&nbsp;
                        <AiOutlineInfoCircle data-tooltip-id={tooltipId}/>&nbsp;&nbsp;
                        <div
                            data-testid="textFoodSocialLegend"
                            className="FoodWidget__header-sub-title"
                            style={{
                                transform: 'translateY(2px)',
                            }}
                        >
                            CLICK BARS TO SEE EXAMPLES
                        </div>
                    </div>
                )}
            >
                <CornerLabel modifiers={['top', 'right', 'no-rem']}>BETA</CornerLabel>
                <div className="FoodSection__row">
                    <FoodWidget
                        headerContent={(<>
                            <div
                                className="FoodSectionSocial__widget-title"
                                data-testid="textFoodSocialTotalMentionsTitle"
                            >
                                TOTAL MENTIONS
                                <div
                                    className="FoodWidget__header-sub-title ml"
                                    data-testid="textFoodSocialTotalMentionsSubTitle"
                                >
                                    &nbsp;past 24 months
                                </div>
                            </div>
                            <FoodTabs
                                data-tooltip-id={tabsTooltipId}
                                data={[
                                    { id: FoodSocialMetricEnum.Mentions, title: FoodSocialMetricEnum.Mentions },
                                    { id: FoodSocialMetricEnum.Engagement, title: FoodSocialMetricEnum.Engagement },
                                ]}
                                activeTabId={selectedMetric}
                                onItemClick={(id) => setSelectedMetric(id as FoodSocialMetricEnum)}
                                testId="blockFoodSocialTotalMentionsTabs"
                            />
                        </>)}
                        className="FoodSectionSocial__widget-1"
                    >
                        <FoodSocialMentions
                            selectedMetric={selectedMetric}
                            socialData={data}
                            selectedMonth={selectedMonth}
                            selectedYear={selectedYear}
                            onMentionBarClick={onMentionBarClick}
                        />
                    </FoodWidget>
                    <div className="FoodSectionSocial__widget-2">
                        <FoodSocialAssociations
                            word={word}
                            selectedAssociation={selectedAssociation}
                            foodAssociations={data.foodAssociations}
                            placesAssociations={data.placesAssociations}
                            onItemClick={onAssociationBarClick}
                        />
                    </div>
                </div>
                <div className="FoodSection__row">
                    <FoodWidget
                        headerContent={(<>
                            <div
                                className="FoodSectionSocial__widget-title"
                                data-testid="textFoodSocialExamplePostsTitle"
                            >
                                EXAMPLE POSTS:&nbsp;{selectedAssociation && <>
                                <b>{selectedAssociation}</b>&nbsp;+&nbsp;</>}{word}
                                <div
                                    className="FoodWidget__header-sub-title ml"
                                    data-testid="textFoodSocialExamplePostsSubTitle"
                                >
                                    &nbsp;{sortedByString},&nbsp;{postsDateRangeString}
                                </div>
                            </div>
                            <div className="FoodSectionSocial__widget-title">
                                <FoodTabs
                                    data={postsTabs}
                                    activeTabId={selectedSortFilterMetric}
                                    onItemClick={(id) => setSelectedSortFilterMetric(id as FoodSocialSortFilterEnum)}
                                    testId="blockFoodSocialExamplePostsTabs"
                                    className="FoodSectionSocial__tabs"
                                />
                                {!isMinimalFiltersFeatureEnabled && (
                                    <div
                                        className="FoodSectionSocial__mini-btn"
                                        data-testid="buttonFoodSocialExamplePostsClearSortBy"
                                        onClick={() => {
                                            setSelectedSortFilterMetric(FoodSocialSortFilterEnum.Engagement);
                                            setSelectedAssociation('');
                                        }}
                                    >
                                        clear
                                    </div>
                                )}
                                <div onClick={notification.handleShowNotification}>
                                    <CopyButton
                                        testId="buttonCopyTable"
                                        data={copyPostsData}
                                    />
                                </div>
                            </div>
                        </>)}
                        className="FoodSectionSocial__widget"
                    >
                        <FoodSocialPosts
                            key={postsSectionKey}
                            data={postsData}
                            word={word}
                            association={selectedAssociation}
                            isLoading={isAssociationPostsLoading}
                        />
                        <CopyNotification isOpen={notification.isOpen}>
                            <span>Data successfully copied</span>
                        </CopyNotification>
                    </FoodWidget>
                </div>
            </FoodSection>
            {!hasAccess && (
                <PermissionOverlay
                    onContactUsClick={() => requestTrial('social')}
                    testId="blockFoodSummaryPermissionOverlay"
                >
                    <div data-testid="textFoodSummaryPermissionOverlayContentLine-0">
                        Upgrade your subscription to unlock
                    </div>
                    <div data-testid="textFoodSummaryPermissionOverlayContentLine-1">
                        insights about <b>{word}</b>
                    </div>
                </PermissionOverlay>
            )}
            <Tooltip
                id={tooltipId}
                place="bottom-start"
            >
                <div className="FoodSectionSocial__tooltip raw-html">
                    <div style={{ marginBottom: 5 }}><b>Data Details</b></div>
                    <ul>
                        <li>updated weekly</li>
                        <li>cleaned and vetted by Datassential trend experts</li>
                        <li>sources include: TikTok, Instagram, Pinterest, YouTube, X, Reddit</li>
                        <li>reflects US & Canadian social activity</li>
                    </ul>
                </div>
            </Tooltip>
            <Tooltip
                id={tabsTooltipId}
                place="bottom"
            >
                <div style={{ width: 170 }}>
                    <div><b>Mentions</b></div>
                    <div>count of total mentions of this term</div>
                    <br/>
                    <div><b>Engagement</b></div>
                    <div>sum of likes, comment, and shares</div>
                </div>
            </Tooltip>
        </div>
    );
};

export default FoodSectionSocial;